import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryExec from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryInt from "@/assets/sprinter-exec-yellow-cabin.jpg?format=webp&quality=72&w=1400&imagetools";
import gallerySuv from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL =
  "https://atlantaluxurysprinters.com/car-service-fulton-county-airport-atlanta";
const PAGE_TITLE = "Car Service Fulton County Airport Atlanta | Brown Field FTY Chauffeur";
const PAGE_DESC =
  "Executive car service at Fulton County Airport (FTY / Brown Field) in Atlanta. Mercedes-Benz Sprinter and SUV chauffeur transportation at Fulton Jet Center and Hill Aircraft FBOs.";

const faqs = [
  {
    q: "Which FBOs do you service at Fulton County Airport?",
    a: "We provide chauffeur service at both Fulton Jet Center and Hill Aircraft, the two main FBOs at Fulton County Airport (FTY / Brown Field).",
  },
  {
    q: "How far is Fulton County Airport from downtown Atlanta?",
    a: "FTY is roughly 15–20 minutes from downtown Atlanta and approximately 25 minutes from Buckhead, depending on traffic.",
  },
  {
    q: "Can you handle corporate group arrivals at FTY?",
    a: "Yes. Mercedes-Benz Sprinter Limousines seat up to 14 passengers, ideal for executive teams, board groups, and entourage arrivals into Fulton County Airport.",
  },
  {
    q: "Is FTY car service available after hours?",
    a: "Yes. Our private aviation team operates 24/7, so late-night and early-morning arrivals at Fulton County Airport are always covered.",
  },
  {
    q: "Where exactly does the chauffeur meet me at Fulton Jet Center or Hill Aircraft?",
    a: "Standard is FBO lobby pickup, the vehicle is staged in the FBO lot before you land and the chauffeur meets you as you come off the ramp. Planeside pickup on the ramp is available at both FBOs when your operator clears it; send the tail number and handler contact and we arrange escort in advance.",
  },
  {
    q: "How early do you stage for an FTY arrival?",
    a: "The vehicle is on the FBO property at least 20 minutes before your ETA, and we track the tail number so an early arrival is already covered. For departures out of FTY we recommend curb-to-cabin 30 minutes before wheels-up on a domestic leg, 60 for international with customs coordination.",
  },
  {
    q: "Can you coordinate directly with my flight department or scheduler?",
    a: "Yes, most FTY work comes through schedulers. We take the tail number, ETA, FBO assignment, passenger count, and bag count, then confirm in writing and send a day-of update with the chauffeur's name and cell before wheels-down.",
  },
  {
    q: "Do you handle crew transportation at FTY?",
    a: "Regularly. Crew hotel runs, meal runs, and next-morning returns are booked as separate legs from the principal's transfer so your passengers and crew never share a vehicle unless you want them to.",
  },
  {
    q: "Is FTY a better choice than PDK for west-side Atlanta?",
    a: "For downtown, West Midtown, Mercedes-Benz Stadium, Vinings, and the west-side corporate corridor, yes, FTY is closer and you skip the I-85 north-side congestion entirely. PDK remains the better field for Buckhead, Brookhaven, Dunwoody, and Perimeter destinations.",
  },
];

export const Route = createFileRoute("/car-service-fulton-county-airport-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "car service Fulton County Airport Atlanta, Fulton County Airport chauffeur, FTY airport car service, Brown Field car service, Fulton Jet Center transportation, Hill Aircraft chauffeur Atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Car Service Fulton County Airport",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Fulton County Airport Chauffeur Service",
            areaServed: [
              { type: "Airport", name: "Fulton County Airport (FTY)" },
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Sandy Springs" },
            ],
          })
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="FTY • Brown Field • Fulton County"
      h1="Car Service at Fulton County Airport"
      intro="Arriving on Atlanta's west side? My chauffeurs coordinate your FTY pickup with Fulton Jet Center or Hill Aircraft, in a Suburban SUV or Mercedes-Benz Sprinter Limousine."
      heroImage={heroImage}
      heroAlt="Private jet on the tarmac at Fulton County Airport (FTY / Brown Field) Atlanta"
      midImage={midImage}
      midAlt="View of a private jet from the FBO terminal window at Fulton County Airport"
      sectionOneTitle="Discreet Chauffeur Service at FTY"
      sectionOneBody={[
        "Fulton County Airport, known as Brown Field, serves private arrivals on Atlanta's west side. We offer scheduled and on-demand chauffeur service at Fulton Jet Center and Hill Aircraft.",
        "We coordinate your tail number, arrival, FBO, passengers and destination with your flight department. Choose a Suburban for a smaller party or a Sprinter for your group.",
      ]}
      bulletsTitle="Why Executives Choose Us at FTY"
      bulletsIntro="A clear pickup plan for your private arrival."
      bullets={[
        "Coverage at Fulton Jet Center and Hill Aircraft",
        "Direct flight department & scheduler coordination",
        "Live tail-number tracking",
        "Mercedes-Benz Sprinter Limo, Executive, and Jet styles",
        "Suburban SUVs for principals, families and smaller parties, up to 6 or 7",
        "24/7 support, including late and early arrivals",
      ]}
      coverageTitle="From FTY to Anywhere in Metro Atlanta"
      coverageIntro="Brown Field's location makes it ideal for fast access to downtown, Buckhead, and the west-side corporate corridor."
      coverage={[
        "Downtown Atlanta, 18 min",
        "Buckhead, 25 min",
        "Midtown, 22 min",
        "West Midtown, 15 min",
        "Mercedes-Benz Stadium, 18 min",
        "Vinings, 15 min",
        "Sandy Springs, 25 min",
        "ATL Airport, 25 min",
        "Marietta, 20 min",
      ]}
      routeTable={{
        title: "FTY Drive Times to Metro Atlanta",
        intro:
          "Typical chauffeured drive times from the Fulton County Airport FBO gate. Brown Field's west-side position keeps you off the north-side interstates entirely.",
        rows: [
          { route: "FTY → Downtown Atlanta", distance: "~10 mi", time: "15–25 min", notes: "I-20 E direct; the fastest private-field run to downtown in Atlanta." },
          { route: "FTY → Mercedes-Benz Stadium", distance: "~10 mi", time: "15–25 min", notes: "Event-day staging arranged in advance; avoid post-game curb queues." },
          { route: "FTY → West Midtown", distance: "~11 mi", time: "15–25 min", notes: "I-20 E to the Connector N; Howell Mill drop points." },
          { route: "FTY → Midtown", distance: "~13 mi", time: "20–30 min", notes: "Connector N to 10th/14th St." },
          { route: "FTY → Buckhead", distance: "~15 mi", time: "25–35 min", notes: "Connector to GA-400; 40–50 min in the 4–7pm peak." },
          { route: "FTY → Vinings / Smyrna", distance: "~10 mi", time: "15–25 min", notes: "I-285 N; quick access to the Cobb corporate corridor." },
          { route: "FTY → Hartsfield-Jackson (ATL)", distance: "~13 mi", time: "20–30 min", notes: "Private-to-commercial connections, including Signature on the south side." },
          { route: "FTY → Sandy Springs / Perimeter", distance: "~22 mi", time: "30–45 min", notes: "I-285 N; PDK is usually the better field for this corridor." },
        ],
        footnote:
          "Every FTY transfer is timed to your tail number, so an early or delayed arrival moves the vehicle automatically.",
      }}
      detailSections={[
        {
          title: "FBO-Specific Pickup at Fulton County Airport",
          body: [
            "FTY has two FBOs, and each handles ground access differently. We confirm your assignment with the handler before every trip so the vehicle is where your aircraft parks.",
          ],
          list: [
            "Fulton Jet Center, vehicle staged in the FBO lot; chauffeur meets you in the lobby, or planeside on the ramp when your operator clears escort.",
            "Hill Aircraft, lobby pickup is standard; ramp access arranged through the line crew with advance notice.",
            "Tail-number tracking on every booking, with the chauffeur on property 20+ minutes before ETA.",
            "Bag transfer handled at the aircraft or the FBO door, passengers never carry their own.",
            "Departures: we recommend arriving 30 minutes before wheels-up domestically, 60 with customs.",
          ],
        },
        {
          title: "Working With Flight Departments and Schedulers",
          list: [
            "We book off tail number, ETA, FBO, passenger count, and bag count, send the trip sheet and we handle the rest.",
            "Written confirmation for every leg, plus a day-of text with the chauffeur's name, cell, and vehicle description.",
            "Separate crew legs, hotel, meal runs, and next-morning returns booked apart from the principal's transfer.",
            "Itinerary changes and diversions to PDK, RYY, or ATL rearranged without re-booking.",
            "Unbranded vehicles and NDA-bound chauffeurs available for high-profile passengers.",
          ],
        },
        {
          title: "Matching the Vehicle to the Aircraft",
          list: [
            "Light jet (2–6 passengers), Suburban SUV covers passengers and bags in one vehicle.",
            "Midsize jet (6–9), Executive Sprinter with forward-facing captain's chairs and work tables.",
            "Heavy jet or group charter (10–14), Sprinter Limousine with lounge seating and separate rear cargo.",
            "Split loads, a Suburban for the principal and a Sprinter for staff and baggage, staged together at the ramp.",
          ],
        },
      ]}
      gallery={{
        title: "Vehicles Staged at FTY",
        intro: "The configurations we bring planeside at Fulton Jet Center and Hill Aircraft.",
        images: [
          { src: galleryExec, alt: "Executive Mercedes-Benz Sprinter staged at a Fulton County Airport FBO in Atlanta", caption: "Executive Sprinter, midsize jet arrivals" },
          { src: galleryInt, alt: "Interior of a Mercedes-Benz Sprinter used for FTY private aviation transfers", caption: "Cabin detailed before every FBO pickup" },
          { src: gallerySuv, alt: "Black Suburban SUV for Fulton County Airport private jet car service", caption: "Suburban SUV, light jet and principal transfers" },
        ],
      }}
      faqTitle="Fulton County Airport (FTY) Car Service FAQs"
      faqs={faqs}
      related={[
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "Our flagship chauffeur service for private aviation.",
        },
        {
          to: "/fbo-transportation-atlanta",
          title: "FBO Transportation Atlanta",
          desc: "Coverage at every major Atlanta FBO.",
        },
        {
          to: "/car-service-pdk-airport",
          title: "Car Service PDK Airport",
          desc: "Sister service at DeKalb-Peachtree.",
        },
      ]}
      ctaTitle="Text Me Your FTY Itinerary"
      ctaBody="Send your tail number, FBO, destination, passengers and bags. I'll check availability myself and send your quote in writing."
    />
  );
}
